'use client';
import React, { useState, useEffect } from 'react';
import { MapPin, Clock, CheckCircle, Zap } from 'lucide-react';
import type { OrderState } from './OrderWizardContent';
import { PLACEMENT_NAMES } from '@/lib/pricingConfig';

const regionNames: Record<string, string> = {
  'fl-jax': 'Jacksonville–North FL',
  'fl-gnv': 'Gainesville–Ocala',
  'fl-orl': 'Orlando / Central FL',
  'fl-lkl': 'Lakeland / Polk',
  'fl-pin': 'Pinellas / St. Pete',
  'fl-tpa': 'Tampa / Hillsborough',
  'fl-sar': 'Sarasota–Bradenton',
  'fl-npl': 'Naples / Fort Myers',
  'fl-mia': 'South Florida',
  'fl-pan': 'Florida Panhandle',
  'pr-sju': 'Metro San Juan',
  'pr-out': 'Outside Metro PR',
};

interface Props {
  order: OrderState;
  currentStep: number;
}

export default function OrderSummaryPanel({ order, currentStep }: Props) {
  const [holdSeconds, setHoldSeconds] = useState(900);
  const isPremium = order.placement !== 'standard';

  useEffect(() => {
    if (!isPremium || order.regions.length === 0) return;
    setHoldSeconds(900);
    const interval = setInterval(() => {
      setHoldSeconds(s => {
        if (s <= 0) { clearInterval(interval); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [order.placement, isPremium, order.regions.length]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Use live price from OrderState (set by StepC from DB), fall back to 0 if not yet selected
  const baseRate = order.placementBasePrice ?? 0;

  // Placement display name: prefer live name from DB stored in state, fall back to static map
  const placementDisplayName = order.placementName || PLACEMENT_NAMES[order.placement] || order.placement;

  const getDiscount = (count: number) => {
    if (order.placement !== 'standard') return 0;
    if (count >= 8) return 0.15;
    if (count >= 5) return 0.10;
    if (count >= 3) return 0.05;
    return 0;
  };

  const discount = getDiscount(order.regions.length);
  const discountedRate = Math.round(baseRate * (1 - discount));
  const subtotal = discountedRate * order.regions.length;
  const designFee = order.addDesign ? 150 : 0;
  const total = subtotal + designFee;

  // Resolve issue display label from order state (single source of truth)
  const issueDisplayLabel = order.issueLabel || (order.issue ? order.issue : '—');
  const artworkDeadlineDisplay = order.issueArtworkDeadline || '—';

  return (
    <div className="sticky top-24 space-y-4">
      {/* Hold timer for premium */}
      {isPremium && order.regions.length > 0 && currentStep >= 2 && (
        <div className={`glass-card rounded-2xl p-4 border ${holdSeconds > 0 ? 'border-amber-500/30 bg-amber-500/8' : 'border-red-500/30 bg-red-500/8'}`}>
          <div className="flex items-center gap-2 mb-1">
            <Clock size={14} className={holdSeconds > 0 ? 'text-amber-400' : 'text-red-400'} />
            <span className="text-xs font-semibold text-amber-400">
              {holdSeconds > 0 ? 'Premium Hold Active' : 'Hold Expired'}
            </span>
          </div>
          {holdSeconds > 0 ? (
            <>
              <p className="font-display font-bold text-2xl text-white tabular-nums">{formatTime(holdSeconds)}</p>
              <p className="text-xs text-slate-400 mt-1">Complete checkout to convert to confirmed reservation</p>
            </>
          ) : (
            <p className="text-xs text-red-400">Your hold has expired. Placement may have been taken.</p>
          )}
        </div>
      )}

      {/* Summary card */}
      <div className="glass-card rounded-2xl p-5 border border-white/8">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={15} className="text-brand-400" />
          <h3 className="font-semibold text-white text-sm">Order Summary</h3>
        </div>

        <div className="space-y-3">
          {/* Market & Issue — uses actual selected issue from state */}
          {currentStep >= 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Market & Issue</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white font-medium">
                  {order.market === 'florida' ? '🌴 Florida' : '🇵🇷 Puerto Rico'}
                </span>
                {order.issue && (
                  <span className="chip text-xs">{issueDisplayLabel}</span>
                )}
              </div>
            </div>
          )}

          {/* Regions */}
          {currentStep >= 1 && order.regions.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Regions ({order.regions.length})
              </p>
              <div className="space-y-1">
                {order.regions.slice(0, 5).map(r => (
                  <div key={`summary-region-${r}`} className="flex items-center gap-1.5">
                    <MapPin size={11} className="text-brand-400 shrink-0" />
                    <span className="text-xs text-slate-300 truncate">{regionNames[r] || r}</span>
                  </div>
                ))}
                {order.regions.length > 5 && (
                  <p className="text-xs text-slate-500 pl-4">+{order.regions.length - 5} more regions</p>
                )}
              </div>
            </div>
          )}

          {/* Placement */}
          {currentStep >= 2 && order.placement && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Placement</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">{placementDisplayName}</span>
                <span className="chip text-xs">{order.term === 'one' ? '1 Issue' : '3-Issue'}</span>
              </div>
            </div>
          )}

          {/* Add-ons */}
          {currentStep >= 3 && order.addDesign && (
            <div className="flex items-center gap-2">
              <CheckCircle size={12} className="text-green-400" />
              <span className="text-xs text-slate-300">Design Service (+$150)</span>
            </div>
          )}

          {/* Artwork */}
          {currentStep >= 4 && order.artworkType && (
            <div className="flex items-center gap-2">
              <CheckCircle size={12} className="text-green-400" />
              <span className="text-xs text-slate-300">
                {order.artworkType === 'upload' ? 'Artwork uploaded' : 'Design brief submitted'}
              </span>
            </div>
          )}

          {/* Agreement */}
          {currentStep >= 5 && order.signatureName && (
            <div className="flex items-center gap-2">
              <CheckCircle size={12} className="text-green-400" />
              <span className="text-xs text-slate-300">Agreement signed</span>
            </div>
          )}
        </div>

        {/* Pricing breakdown */}
        {order.regions.length > 0 && order.placement && (
          <div className="mt-5 pt-4 border-t border-white/8 space-y-2">
            {discount > 0 ? (
              <>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">{placementDisplayName} × {order.regions.length}</span>
                  <span className="text-slate-500 tabular-nums line-through">${baseRate.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-green-400">Multi-region discount ({(discount * 100).toFixed(0)}%)</span>
                  <span className="text-green-400 tabular-nums">-${Math.round(baseRate * discount * order.regions.length).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Subtotal after discount</span>
                  <span className="text-slate-300 tabular-nums">${subtotal.toLocaleString()}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">{placementDisplayName} × {order.regions.length}</span>
                <span className="text-slate-300 tabular-nums">${discountedRate.toLocaleString()}</span>
              </div>
            )}
            {designFee > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Design Service</span>
                <span className="text-slate-300 tabular-nums">$150</span>
              </div>
            )}
            {order.couponDiscount > 0 && order.couponCode && (
              <div className="flex justify-between text-xs">
                <span className="text-green-400">Coupon ({order.couponCode})</span>
                <span className="text-green-400 tabular-nums">-${order.couponDiscount.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-white/8">
              <span className="text-sm font-semibold text-white">Total{order.term === 'three' ? ' / mo' : ''}</span>
              <span className="font-display font-bold text-lg text-brand-400 tabular-nums">
                ${(order.couponFinalTotal !== null ? order.couponFinalTotal : total).toLocaleString()}
              </span>
            </div>
            {order.term === 'three' && (
              <p className="text-xs text-slate-500">Billed monthly × 3 issues = ${((order.couponFinalTotal !== null ? order.couponFinalTotal : total) * 3).toLocaleString()} total</p>
            )}
          </div>
        )}
      </div>

      {/* Artwork deadline reminder — uses actual selected issue deadline */}
      {order.issue && (
        <div className="glass-card rounded-xl p-4 border border-amber-500/20 flex items-start gap-2.5">
          <Clock size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-amber-400">Artwork Deadline</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {artworkDeadlineDisplay}
              {order.issuePrintDate ? ` — 7 days before print` : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}